import { formatDistance } from "date-fns";
import { es } from "date-fns/locale";
import { capitalize } from "lodash";
import moment from "moment";
import { FC, useCallback, useState } from "react";
import { HiOutlineEye } from "react-icons/hi";
import { Link } from "react-router-dom";
import { ActionIcon, Badge, Switch, Tooltip } from "rizzui";
import { toast } from "sonner";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import CustomTable from "../../../components/table/CustomTable";
import useFetch from "../../../hooks/useFetch";
import Page from "../../../layout/Page/Page";
import { menuRoutes } from "../../../router/menu";
import { MdOutlineModeEditOutline, MdAdd } from "react-icons/md";
import { FormDrawerView } from "../../../components/forms/FormDrawer";
import { Product, ProductsApiResponse } from "../../../type/entities/product-type";
import { ProductService } from "../../../services/product/productService";
import ProductsFilters from "./ProductsFilters";
import ProductForm from "../components/ProductForm";
import AlertsProduct from "../components/AlertsProduct";
import AddStockModal from "../components/AddStockModal";

interface ProductListProps {
    openFormDrawer: boolean;
    setOpenFormDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    productSelected: Product | null;
    setProductSelected: React.Dispatch<React.SetStateAction<Product | null>>;
}

const columnOrder = [
    'id',
    'company',
    'name',
    'description',
    'category',
    'unitMeasure',
    'currentStock',
    'minimumStock',
    'purchasePrice',
    'active',
    'createdAt',
    'updatedAt'
];

const ProductList: FC<ProductListProps> = ({ openFormDrawer, setOpenFormDrawer, productSelected, setProductSelected }) => {

    // STATES

    const [changingStatus, setChangingStatus] = useState<string[]>([]);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [openAddStockModal, setOpenAddStockModal] = useState<boolean>(false);
    const [selectedProductForStock, setSelectedProductForStock] = useState<Product | null>(null);
    const service = new ProductService();

    // HOOKS

    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE PRODUCTOS
     * @EN GETS THE LIST OF PRODUCTS
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listProducts(filters);
        return response.getResponseData() as ProductsApiResponse;
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE PRODUCTOS CON STOCK POR DEBAJO DEL MINIMO
     * @EN GETS THE LIST OF PRODUCTS WITH STOCK BELOW THE MINIMUM
     */
    // -------------------------------------------------------------------------------------------------------------
    const [dataAlert, loadingAlert, errorAlert, refetchAlert] = useFetch(useCallback(async () => {
        const response = await service.listAlertStock();
        return response.getResponseData() as ProductsApiResponse;
    }, []));
    // -------------------------------------------------------------------------------------------------------------

    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES TOGGLE STATUS DEL PRODUCTO
     * @EN TOGGLE PRODUCT STATUS
     */
    //-------------------------------------------------------------------------------------------------------------
    const toggleStatus = async (id: string, status: boolean) => {
        try {
            setChangingStatus([...changingStatus, id]);
            const response = (await service.toggleProductStatus(id, status)).getResponseData(); 
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                refetchAlert();
                toast.success(response.message);
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (<>

                <AlertsProduct data={dataAlert} updateFilters={updateFilters} refetchAlerts={refetchAlert} refetch={refetch}/>

                <CustomTable
                    id={'products-table'}
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    columnOrder={columnOrder}
                    data={orderedData()}
                    floatingHorizontalScrollbar
                    isLoading={loading}
                    isSortable
                    columnsNoSortables={['active']}
                    columnsNotShown={[
                        'description',
                        'unitMeasure',
                        'minimumStock',
                    ]}
                    overrideColumns={[
                        {
                            key: 'company',
                            columnNameKey: 'Empresa',
                            permissions: [{
                                group: 'company',
                                permission: 'list'
                            }],
                            render: (row: any) => {
                                if (!row.row.original?.company) return 'N/A';
                                return (
                                    <div>
                                        {row.row.original?.company?.name}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'name',
                            columnNameKey: 'Nombre',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original?.name}
                                        <div className="text-gray-400">
                                            {row.row.original?.description || 'N/A'}
                                        </div>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'category',
                            columnNameKey: 'Categoría',
                            render: (row: any) => {
                                return (
                                    <Badge
                                        size="sm"
                                        style={{ backgroundColor: row.row.original?.category?.color }}
                                        variant="solid"
                                    >
                                        {row.row.original?.category?.name || 'N/A'}
                                    </Badge>
                                );
                            }
                        },
                        {
                            key: 'currentStock',
                            columnNameKey: 'Stock actual',
                            render: (row: any) => {
                                return (
                                    <div>
                                        {row.row.original?.currentStock} <span className="text-gray-400">{row.row.original?.unitMeasure?.name}</span>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'purchasePrice',
                            columnNameKey: 'Precio de compra',
                            render: (row: any) => {
                                return (
                                    <div>
                                        €{row.row.original?.purchasePrice}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'active',
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={row.row.original.active ? 'Desactivar' : 'Activar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Switch
                                            id={row.row.original.id}
                                            checked={row.row.original.active}
                                            disabled={changingStatus.includes(row.row.original.id)}
                                            onChange={() => toggleStatus(row.row.original.id, row.row.original.active)}
                                            switchKnobClassName='bg-secondary'
                                        />
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            key: 'createdAt',
                            render: (row: any) => {
                                const date = row.row.original?.createdAt?.date;
                                return date ? moment(date).format('DD/MM/YYYY') : 'N/A';
                            },
                        },
                        {
                            key: 'updatedAt',
                            render: (row: any) => {
                                const date = row.row.original?.updatedAt?.date;
                                return date ? (
                                    <div className="text-sm text-gray-500">
                                        <Tooltip content={moment(date).format('DD/MM/YYYY HH:mm:ss')}>
                                            <span>{capitalize(formatDistance(new Date(date), new Date(), { addSuffix: true, locale: es }))}</span>
                                        </Tooltip>
                                    </div>
                                ) : 'Nunca';
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'View',
                            permissions: { group: 'product', permission: 'get' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver perfil'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link
                                            to={`${menuRoutes.products.path}/${row.id}/profile/${menuRoutes.products.profile.info}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white w-6 h-6"
                                            >
                                                <HiOutlineEye className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'AddStock',
                            permissions: { group: 'stock_movements', permission: 'create' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Añadir Stock'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setSelectedProductForStock(row);
                                                setOpenAddStockModal(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-green-600 hover:text-green-700 bg-white"
                                        >
                                            <MdAdd className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'product', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setProductSelected(row);
                                                setOpenFormDrawer(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white w-6 h-6"
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as ProductsApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            </>)}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Productos'} onClear={() => resetFilters(50)}>
                <ProductsFilters filters={filters} updateFilters={updateFilters} />
            </FilterDrawerView>

            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    productSelected ?
                        "Completa el formulario para editar los datos del producto" :
                        "Completa el formulario para crear un nuevo producto"
                }
                drawerTitle={productSelected ? 'Editar Producto' : 'Crear Producto'} submitText={productSelected ? 'Guardar Producto' : 'Crear Producto'}>
                <ProductForm setOpenModal={setOpenFormDrawer} product={productSelected} refetch={refetch} refetchAlert={refetchAlert} />
            </FormDrawerView>

            {selectedProductForStock && (
                <AddStockModal
                    isOpen={openAddStockModal}
                    setIsOpen={setOpenAddStockModal}
                    product={selectedProductForStock}
                    refetch={() => {
                        refetch();
                        refetchAlert();
                    }}
                />
            )}

        </Page>
    );
};

export default ProductList;